import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const bars      = [68, 95, 56, 130, 88, 112, 70, 148];
const barColors = ['#ddd6fe','#ddd6fe','#ddd6fe','#ddd6fe','#a78bfa','#ddd6fe','#ddd6fe','#7c3aed'];

export default function IlluAnalytics() {
    const svgRef = useRef(null);
    const maxH   = 80;
    const scale  = maxH / Math.max(...bars);
    const base   = 204;

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        const ctx = gsap.context(() => {
            const header    = svg.querySelector('#an-header');
            const badge     = svg.querySelector('#an-badge');
            const pill      = svg.querySelector('#an-pill');
            const kpis      = svg.querySelectorAll('.an-kpi');
            const barEls    = svg.querySelectorAll('.an-bar');
            const dayLabels = svg.querySelectorAll('.an-day');
            const caption   = svg.querySelector('#an-caption');
            const pulseRing = svg.querySelector('#an-pulse-ring');
            const scanner   = svg.querySelector('#an-scanner');

            // ── initial states ──
            gsap.set([header, pill], { opacity: 0 });
            gsap.set(badge,    { opacity: 0, scale: 0, transformBox: 'fill-box', transformOrigin: '50% 50%' });
            gsap.set(kpis,     { opacity: 0, y: 20 });
            gsap.set(barEls,   { scaleY: 0, transformBox: 'fill-box', transformOrigin: '50% 100%' });
            gsap.set(dayLabels,{ opacity: 0 });
            gsap.set(caption,  { opacity: 0 });
            gsap.set(scanner,  { opacity: 0 });

            // ── entry timeline ──
            const tl = gsap.timeline({
                scrollTrigger: { trigger: svg, start: 'top 85%', once: true },
                onComplete: startLoops,
            });

            tl
                .to(header, { opacity: 1, duration: 0.5, ease: 'power2.out' })
                .to(badge,  { opacity: 1, scale: 1, duration: 0.45, ease: 'back.out(2.2)' }, '-=0.15')
                .to(pill,   { opacity: 1, duration: 0.35, ease: 'power2.out' }, '-=0.2')
                .to(kpis,   { opacity: 1, y: 0, duration: 0.5, ease: 'expo.out', stagger: 0.1 }, '-=0.15')
                .to(barEls, { scaleY: 1, duration: 0.65, ease: 'expo.out', stagger: 0.07 }, '-=0.15')
                .to(dayLabels, { opacity: 1, duration: 0.3, ease: 'power1.out', stagger: 0.04 }, '-=0.3')
                .to(caption, { opacity: 1, duration: 0.4, ease: 'power2.out' }, '-=0.1');

            function startLoops() {

                // 1. Green live-dot pulse — smooth sine, radius-based
                gsap.fromTo(pulseRing,
                    { attr: { r: 7 }, opacity: 0.5 },
                    { attr: { r: 15 }, opacity: 0, duration: 1.4, ease: 'sine.out', repeat: -1, repeatDelay: 0.7 }
                );

                // 2. Bar wave — each bar oscillates with a left-to-right phase offset
                Array.from(barEls).forEach((bar, i) => {
                    gsap.to(bar, {
                        scaleY: 1.10,
                        transformBox: 'fill-box', transformOrigin: '50% 100%',
                        duration: 0.9 + i * 0.04,
                        ease: 'sine.inOut',
                        yoyo: true, repeat: -1,
                        delay: i * 0.12,
                    });
                });

                // 3. Vertical scanner sweeps down through the chart zone
                function runScanner() {
                    gsap.timeline({ onComplete: () => gsap.delayedCall(2.0, runScanner) })
                        .fromTo(scanner,
                            { attr: { y1: 124, y2: 124 }, opacity: 0 },
                            { attr: { y1: 160, y2: 160 }, opacity: 0.7, duration: 0.6, ease: 'power2.in' }
                        )
                        .to(scanner,
                            { attr: { y1: 204, y2: 204 }, opacity: 0, duration: 0.9, ease: 'power1.out' }
                        );
                }
                runScanner();

                // 4. Badge gentle pulse
                gsap.to(badge, {
                    scale: 1.06, transformBox: 'fill-box', transformOrigin: '50% 50%',
                    duration: 1.8, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 0.3,
                });

                // 5. KPI cards staggered float
                gsap.to(kpis, {
                    y: -3, duration: 2.0, ease: 'sine.inOut', yoyo: true, repeat: -1,
                    stagger: { each: 0.45 },
                });
            }
        }, svgRef);

        return () => ctx.revert();
    }, []);

    return (
        <svg ref={svgRef} viewBox="0 0 460 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="pxl-al-illu-svg">
            <defs>
                <linearGradient id="an-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop stopColor="#f0eaff"/><stop offset="1" stopColor="#e6deff"/>
                </linearGradient>
                {/* Scanner gradient — bright centre, transparent edges */}
                <linearGradient id="an-scan-grad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0"   stopColor="#7c3aed" stopOpacity="0"/>
                    <stop offset="0.2" stopColor="#a78bfa" stopOpacity="0.6"/>
                    <stop offset="0.5" stopColor="#c4b5fd" stopOpacity="1"/>
                    <stop offset="0.8" stopColor="#a78bfa" stopOpacity="0.6"/>
                    <stop offset="1"   stopColor="#7c3aed" stopOpacity="0"/>
                </linearGradient>
                <filter id="an-shadow" x="-5%" y="-10%" width="110%" height="130%">
                    <feDropShadow dx="0" dy="6" stdDeviation="12" floodColor="#4c1d95" floodOpacity="0.09"/>
                </filter>
                {/* Clip all card content to the card boundary so pulse stays inside */}
                <clipPath id="an-card-clip">
                    <rect x="18" y="14" width="424" height="252" rx="18"/>
                </clipPath>
            </defs>

            <rect width="460" height="280" fill="url(#an-bg)"/>
            {/* Shadow rendered separately so it isn't clipped */}
            <rect x="18" y="14" width="424" height="252" rx="18" fill="#fff" filter="url(#an-shadow)"/>

            {/* All card content clipped to card boundary */}
            <g clipPath="url(#an-card-clip)">

            {/* Header */}
            <g id="an-header">
                <circle id="an-pulse-ring" cx="38" cy="42" r="7" fill="#22c55e" opacity="0"/>
                <circle cx="38" cy="42" r="10" fill="#22c55e" opacity="0.18"/>
                <circle cx="38" cy="42" r="6"  fill="#22c55e"/>
                <text x="52" y="47" fontSize="13" fontWeight="700" fill="#1a1535" fontFamily="inherit">Live Analytics</text>
            </g>

            <g id="an-badge">
                <rect x="210" y="30" width="80" height="24" rx="12" fill="#1a1535"/>
                <text x="250" y="46" textAnchor="middle" fontSize="9.5" fill="#fff" fontWeight="700" fontFamily="inherit">+148 today</text>
            </g>

            <g id="an-pill">
                <rect x="334" y="30" width="90" height="24" rx="12" fill="#f3f0fc"/>
                <text x="379" y="46" textAnchor="middle" fontSize="10" fill="#7c3aed" fontWeight="600" fontFamily="inherit">This week ▾</text>
            </g>

            <line x1="34" y1="62" x2="426" y2="62" stroke="#f0ecfc" strokeWidth="1"/>

            {/* KPI cards */}
            {[
                ['Clicks',  '2,840', '+12%', '#f5f0ff', '#7c3aed'],
                ['Signups',   '143',  '+8%', '#f0fdf4', '#16a34a'],
                ['Revenue', '$3,420', '+22%', '#fff7ed', '#d97706'],
            ].map(([lbl, val, chg, bg, color], i) => (
                <g key={i} className="an-kpi">
                    <rect x={34 + i*134} y="70" width="122" height="52" rx="12" fill={bg}/>
                    <text x={50 + i*134} y="87"  fontSize="10"   fill="#6b7280" fontFamily="inherit">{lbl}</text>
                    <text x={50 + i*134} y="112" fontSize="17" fontWeight="800" fill="#1a1535" fontFamily="inherit">{val}</text>
                    <rect x={106 + i*134} y="76" width="36" height="15" rx="7" fill={color} opacity="0.15"/>
                    <text x={124 + i*134} y="87" textAnchor="middle" fontSize="9" fill={color} fontWeight="700" fontFamily="inherit">{chg} ↑</text>
                </g>
            ))}

            {/* Chart baseline */}
            <line x1="34" y1={base} x2="426" y2={base} stroke="#ebe8f8" strokeWidth="1"/>

            {/* Scanner line — animated vertically through chart zone */}
            <line id="an-scanner" x1="42" y1="124" x2="426" y2="124"
                stroke="url(#an-scan-grad)" strokeWidth="2" opacity="0"/>

            {/* Bars */}
            {bars.map((h, i) => {
                const sh = Math.round(h * scale);
                return (
                    <rect
                        key={i}
                        className="an-bar"
                        x={42 + i*48} y={base - sh}
                        width="32" height={sh}
                        rx="7" fill={barColors[i]}
                    />
                );
            })}

            {/* Day labels */}
            {['M','T','W','T','F','S','S','T'].map((d, i) => (
                <text key={i} className="an-day" x={58 + i*48} y="220"
                    textAnchor="middle" fontSize="9.5" fill="#9ca3af" fontFamily="inherit">{d}</text>
            ))}

            <text id="an-caption" x="230" y="246" textAnchor="middle" fontSize="9.5" fill="#9ca3af" fontFamily="inherit">
                Weekly performance · updates in real time
            </text>

            </g>{/* end card clip */}
        </svg>
    );
}
